import type { RorConsent } from "../consent";

export const ADSENSE_CLIENT_ID = "ca-pub-4575081687240457";
export const ADSENSE_SCRIPT_ID = "ror-adsense-loader";

// REN-47: single Display-responsive slot mounted at the bottom of the
// results view. The calculator input form stays ad-free — only the
// answer/chart/panels region receives this slot.
export const AD_SLOT_RESULTS_DISPLAY = "9075325067";

interface AdsByGoogleWindow {
  adsbygoogle?: unknown[];
}

// Build the `<ins>` block for the results-page display slot and enqueue
// the standard `push({})` so AdSense binds to it once the loader arrives.
// Kept side-effect-light and idempotent per call: it never touches the
// DOM outside the returned element, and the push is a no-op if the same
// element is re-inserted (AdSense marks used slots with data-adsbygoogle-status).
export function createResultsDisplayAdSlot(
  doc: Document = document,
  win: Window | null = typeof window === "undefined" ? null : window,
  clientId: string = ADSENSE_CLIENT_ID,
  slotId: string = AD_SLOT_RESULTS_DISPLAY,
): HTMLElement {
  const wrap = doc.createElement("aside");
  wrap.className = "results-ad-slot";
  wrap.setAttribute("role", "complementary");
  wrap.setAttribute("aria-label", "Advertisement");
  wrap.dataset.adSlot = "results-display";

  const ins = doc.createElement("ins");
  ins.className = "adsbygoogle";
  ins.style.display = "block";
  ins.setAttribute("data-ad-client", clientId);
  ins.setAttribute("data-ad-slot", slotId);
  ins.setAttribute("data-ad-format", "auto");
  ins.setAttribute("data-full-width-responsive", "true");
  wrap.appendChild(ins);

  if (win) {
    const w = win as unknown as AdsByGoogleWindow;
    const queue = (w.adsbygoogle = w.adsbygoogle ?? []);
    queue.push({});
  }
  return wrap;
}

export interface AdSenseLoaderDeps {
  consent?: RorConsent | null;
  doc?: Document;
  win?: Window;
  clientId?: string;
}

type GtagFn = (...args: unknown[]) => void;

// Per Google's Consent Mode v2 guidance (REN-51): the AdSense loader is placed
// in <head> of content pages unconditionally so the review crawler can see it;
// serving is gated at the ad-network layer via gtag('consent', ...) signals.
// Defaults are declared inline (denied) in each content page's HTML before
// this module runs. This function only pushes the "update" when the user
// grants advertising consent via our CMP. Kept idempotent so it is safe to
// call from every page load.
export function loadAdSenseOnConsent(deps: AdSenseLoaderDeps = {}): void {
  const consent = deps.consent ?? (typeof window === "undefined" ? null : window.rorConsent ?? null);
  const doc = deps.doc ?? (typeof document === "undefined" ? null : document);
  const win = deps.win ?? (typeof window === "undefined" ? null : window);
  const clientId = deps.clientId ?? ADSENSE_CLIENT_ID;
  if (!consent || !doc) return;
  consent.onGrant("advertising", () => {
    injectAdSenseScript(doc, clientId);
    updateAdConsentGranted(win);
  });
}

// Emit the loader script into <head>. On the shipped content pages the inline
// tag already exists with the same id, so this call becomes a no-op — kept as
// a safety net for any page that hasn't inlined the tag (and for the test
// suite, which asserts injection behavior directly).
export function injectAdSenseScript(doc: Document, clientId: string): HTMLScriptElement | null {
  if (doc.getElementById(ADSENSE_SCRIPT_ID)) return null;
  const script = doc.createElement("script");
  script.id = ADSENSE_SCRIPT_ID;
  script.async = true;
  script.crossOrigin = "anonymous";
  script.src =
    "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=" +
    encodeURIComponent(clientId);
  doc.head.appendChild(script);
  return script;
}

export function updateAdConsentGranted(win: Window | null): void {
  if (!win) return;
  const gtag = (win as unknown as { gtag?: GtagFn }).gtag;
  if (typeof gtag !== "function") return;
  gtag("consent", "update", {
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "granted",
  });
}
